import { Component, HostListener, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { TemplateEditorComponent } from '../template-editor.component/template-editor.component'
import { ActivatedRoute, Router } from '@angular/router'
import { map, switchMap, firstValueFrom, filter, lastValueFrom, merge, Subject } from 'rxjs'
import { AsyncPipe, DatePipe } from '@angular/common'
import { Template, TemplateItem } from '../template-editor.component/types/template.types'
import { ToastService } from '../toast.service'
import { TemplatesService } from '../templates.service'
import { CallResource, CallsService, MinutesAnonymization, MinutesVerboseness } from '../calls.service'
import { MatDialog } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { TemplateImportDialog } from '../template-editor.component/template-import-dialog/template-import-dialog'
import { TemplateSaveAsDialog } from '../template-editor.component/template-save-as-dialog/template-save-as-dialog'
import { TemplatePreviewPanelComponent } from '../template-editor.component/template-preview-panel/template-preview-panel.component'
import { MultiFileUploadComponent } from '../multi-file-upload/multi-file-upload.component'
import { FileUploadRequest } from '../template-editor.component/template-item/template-item.component'
import { getUploadErrorMessage, UploadsService } from '../uploads.service'
import { LoggerService } from '../logger.service'
import { MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES } from '../call-document-upload/call-document-upload.component'
import { FileValidationService } from '../file-validation.service'
import { getFileExtension, getFileName, truncateStringFromMiddle } from '../utils'
import { FilesizePipe } from '../filesize.pipe'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { PageCountPipe } from '../page-count.pipe'
import { DurationPipe } from '../duration.pipe'
import { PdfPreviewDialogComponent, PdfPreviewDialogData } from '../template-editor.component/pdf-preview-dialog/pdf-preview-dialog.component'
import { ComponentWithUnsavedChanges } from '../unsaved-changes.guard'

@Component({
  selector: 'app-template-playground-page',
  imports: [
    TemplateEditorComponent,
    AsyncPipe,
    DatePipe,
    DurationPipe,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    TemplatePreviewPanelComponent,
    MultiFileUploadComponent,
    FilesizePipe,
    PageCountPipe,
  ],
  standalone: true,
  templateUrl: './template-playground-page.html',
  styleUrl: './template-playground-page.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [FilesizePipe],
})
export class TemplatePlaygroundPage implements ComponentWithUnsavedChanges {
  @ViewChild('templateEditor')
  templateEditor!: TemplateEditorComponent

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  get hasUnsavedChanges(): boolean {
    return this.templateEditor?.hasUnsavedChanges ?? false
  }

  private templateService = inject(TemplatesService)
  private callsService = inject(CallsService)
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private dialog = inject(MatDialog)
  private uploadsService = inject(UploadsService)
  private toastService = inject(ToastService)
  private loggerService = inject(LoggerService)
  private fileValidationService = inject(FileValidationService)
  private translateService = inject(TranslateService)

  callId$ = this.route.paramMap.pipe(map((params) => parseInt(params.get('id')!)))
  updateTemplateItemsMutation = this.templateService.updateTemplateItemsSuper()
  assignTemplateMutation = this.callsService.assignTemplateMutation()
  removeResourceMutation = this.callsService.removeResource()
  importResourceMutation = this.callsService.importResource()
  saveAsTemplateMutation = this.templateService.saveCallTemplateAsModel()

  maximumCustomDocumentSize = MAX_CUSTOM_DOCUMENT_SIZE_IN_BYTES

  private templateItemsChanges$ = new Subject<TemplateItem[]>()

  call$ = this.route.paramMap.pipe(
    map((params) => params.get('id')),
    map((id) => (id !== null ? parseInt(id, 10) : null)),
    filter((id) => id !== null),
    switchMap((id) => this.callsService.getCall(id))
  )

  templateItems$ = this.callId$.pipe(switchMap((id) => this.templateService.getTemplateItems(id)))

  liveTemplateItems$ = merge(
    this.templateItems$.pipe(
      filter((result) => result.isSuccess),
      map((result) => result.data)
    ),
    this.templateItemsChanges$
  )

  templateModels$ = this.templateService.getTemplateModels()

  customDocuments$ = this.call$.pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data!.userUploadedResources.filter((resource) => resource.resourceType === 'CUSTOM_DOCUMENT'))
  )

  onTemplateItemsChanged(items: TemplateItem[]) {
    this.templateItemsChanges$.next(items)
  }

  async saveTemplate(items: TemplateItem[]) {
    const callId = await firstValueFrom(this.callId$)
    try {
      const response = await this.updateTemplateItemsMutation.mutateAsync({ callId, items })
      this.templateEditor.confirmSaveSuccess()
      this.templateEditor.applyIdMapping(response.clientIdToServerIdMapping)
    } catch (error) {
      this.loggerService.error('Failed to save template:', error)
      this.templateEditor.confirmSaveFailure()
    }
  }

  async importTemplate(templates: Template[]) {
    if (this.hasUnsavedChanges) {
      const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant('TEMPLATES.UNSAVED_CHANGES_DIALOG.TITLE'),
          description: this.translateService.instant('TEMPLATES.UNSAVED_CHANGES_DIALOG.IMPORT_DESCRIPTION'),
          confirmLabel: this.translateService.instant('TEMPLATES.UNSAVED_CHANGES_DIALOG.DISCARD'),
          cancelLabel: this.translateService.instant('COMMON.CANCEL'),
        },
      })

      const confirmed = await firstValueFrom(confirmDialogRef.afterClosed())
      if (!confirmed) {
        return
      }
    }

    const callId = await firstValueFrom(this.callId$)
    const dialogRef = this.dialog.open(TemplateImportDialog, {
      data: { templates },
      width: '500px',
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.templateEditor.confirmSaveSuccess()
        await this.assignTemplateMutation.mutateAsync({ callId, templateId: result.templateId })
      }
    })
  }

  async saveAsTemplate() {
    const organizationId = await firstValueFrom(this.call$.pipe(map((result) => result.data!.organizationId)))
    const dialogRef = this.dialog.open(TemplateSaveAsDialog, {
      width: '500px',
    })

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.saveAsTemplateMutation.mutateAsync({ organizationId, name: result.name, items: this.templateEditor.templateItems })
      }
    })
  }

  async removeDocument(resource: CallResource) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('TEMPLATES.REMOVE_DOCUMENT_DIALOG.TITLE'),
        description: this.translateService.instant('TEMPLATES.REMOVE_DOCUMENT_DIALOG.DESCRIPTION', { fileName: resource.fileName }),
        confirmLabel: this.translateService.instant('COMMON.DELETE'),
        cancelLabel: this.translateService.instant('COMMON.CANCEL'),
      },
    })

    dialogRef.afterClosed().subscribe(async (confirmed) => {
      if (confirmed) {
        const callId = await firstValueFrom(this.callId$)
        await this.removeResourceMutation.mutateAsync({ callId, resourceType: resource.resourceType, index: resource.index, extension: resource.extension })
      }
    })
  }

  async handleFileUpload(request: FileUploadRequest, callId: number) {
    const { blockId, file } = request
    const fileIsValid = this.fileValidationService.validateFile(file, ['pdf'], this.maximumCustomDocumentSize)

    if (!fileIsValid) {
      return
    }

    const extension = getFileExtension(file)
    const fileName = getFileName(file)

    this.templateEditor.addPendingSourceToBlock(blockId, fileName)

    try {
      const uploadInfo = await lastValueFrom(this.uploadsService.uploadCustomDocument(file))
      if (uploadInfo.uploadKey === null) {
        throw new Error('Upload key not received for file upload with name ' + fileName)
      }
      const createdResource = await this.importResourceMutation.mutateAsync({
        callId,
        data: {
          uploadKey: uploadInfo.uploadKey,
          fileExtension: extension.toUpperCase(),
          fileType: 'CUSTOM_DOCUMENT',
          fileName,
        },
      })
      this.templateEditor.addSourceToBlock(blockId, createdResource, fileName)
    } catch (error) {
      this.loggerService.error('Error uploading file', error)
      this.templateEditor.removePendingSourceFromBlock(blockId, fileName)
      const uploadErrorMessage = getUploadErrorMessage(error)
      if (uploadErrorMessage !== null) {
        this.toastService.show(uploadErrorMessage)
      } else {
        this.toastService.showRaw(this.translateService.instant('TEMPLATES.EDITOR.FILE_UPLOAD_FAILED'))
      }
    }
  }

  onPreviewItemClicked(itemId: number) {
    this.templateEditor.selectItem(itemId)
    this.templateEditor.scrollToItem(itemId)
  }

  truncateFileNameFromMiddle(fileName: string, maxLength: number = 20): string {
    return truncateStringFromMiddle(fileName, maxLength)
  }

  openPdfPreview(resource: CallResource): void {
    if (resource.downloadUrl === null) {
      this.loggerService.error('Download URL is missing. Cannot open PDF preview.')
      return
    }
    this.dialog.open(PdfPreviewDialogComponent, {
      width: '80vw',
      maxWidth: '900px',
      height: '85vh',
      data: {
        downloadUrl: resource.downloadUrl,
        fileName: resource.fileName,
      } as PdfPreviewDialogData,
    })
  }

  goBackToCall(callId: number): void {
    this.router.navigate(['/app', 'meetings', callId], { queryParams: { tab: 'template' } })
  }
}
